/** Console v0.5: how figures, dates and greetings are written (PRODUCT-UI.md s.5-6). Pure -- tested with Node
 * (web/console/test/v05-format.test.ts). */

/** $18,402.50; negatives in brackets ($6,207.50). From whole cents (A: money is cents). */
export function money(cents: number): string {
  const neg = cents < 0;
  const abs = Math.abs(Math.round(cents));
  const dollars = Math.floor(abs / 100).toLocaleString("en-AU");
  const s = `$${dollars}.${String(abs % 100).padStart(2, "0")}`;
  return neg ? `(${s})` : s;
}

/** A change against last period: +$1,200.00 / ($310.50) / — when there's nothing to compare. */
export function delta(cents: number | null): string {
  if (cents === null) return "—";
  if (cents === 0) return "$0.00";
  return cents > 0 ? `+${money(cents)}` : money(cents);
}

function ymd(d: Date, timeZone: string): string {
  // en-CA writes YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(d);
}

/** A due date in lists: "Today", "Tomorrow", "Overdue · 21 Oct", "28 Oct", or "No due date". */
export function dueLabel(due: string | null, now: Date, timeZone: string): string {
  if (!due) return "No due date";
  const today = ymd(now, timeZone);
  const tomorrow = ymd(new Date(now.getTime() + 86_400_000), timeZone);
  const short = shortDay(due);
  if (due === today) return "Today";
  if (due === tomorrow) return "Tomorrow";
  if (due < today) return `Overdue · ${short}`;
  return short;
}

/** "28 Oct" from an ISO date (a calendar date: no time zone shift). */
export function shortDay(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d} ${months[m - 1]}`;
}

/** "Friday 23 October" in the viewer's zone. */
export function longToday(now: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-AU", { weekday: "long", day: "numeric", month: "long", timeZone }).format(now).replace(",", "");
}

/** Good morning (before 12), Good afternoon (before 6 pm), Good evening, in the viewer's zone. */
export function greeting(now: Date, timeZone: string, name: string | null): string {
  const hour = Number(new Intl.DateTimeFormat("en-AU", { hour: "numeric", hourCycle: "h23", timeZone }).format(now));
  const part = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const first = name?.trim().split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}
